import { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardPageFrame from '../../dashboard/components/DashboardPageFrame.jsx';
import MasterCrudTable from '../../dashboard/components/MasterCrudTable.jsx';
import { USER_DEFINITIONS, MASTER_GROUPS, getMasterDefinition } from '../../dashboard/constants/masterDefinitions.js';
import { getMasterItems } from '../../dashboard/api/dashboardApi.js';
import { canAdmin, canSuperAdmin, canSystemAdmin } from '../../../auth/permissions.js';
import '../../dashboard/styles/dashboard-master.css';

const ACTIVE_FUNCTION = 'AWS利用料';

function getFeatureTabs() {
  const tabs = [
    { label: 'Dashboard', href: '/dashboard/master' },
    { label: 'Life', href: '/meyasu/master' },
    { label: 'AWS利用料', href: '/aws-billing/master' },
  ];
  if (canSystemAdmin()) {
    tabs.push({ label: 'システム管理', href: '/system/master' });
  }
  return tabs;
}

const SUPER_ADMIN_KEYS = new Set(['functions', 'permissions']);
const USER_GROUP = MASTER_GROUPS.find((g) => g.colorClass === 'user');

export default function AwsBillingMasterPage() {
  const [allData, setAllData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeKey, setActiveKey] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const endpoints = ['users', 'permission_managements', 'functions', 'permissions'];
      const results = await Promise.all(
        endpoints.map((ep) => getMasterItems(ep)),
      );
      const data = {};
      endpoints.forEach((ep, i) => { data[ep] = results[i]; });
      setAllData(data);
    } catch (err) {
      setError(err.message || 'データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const functionRecord = useMemo(
    () => (allData.functions || []).find((f) => f.function === ACTIVE_FUNCTION),
    [allData.functions],
  );

  const maxAssignableId = canSystemAdmin() ? 5 : (canSuperAdmin(ACTIVE_FUNCTION) ? 3 : (canAdmin(ACTIVE_FUNCTION) ? 2 : 0));
  const assignablePermissions = useMemo(
    () => (allData.permissions || []).filter((p) => p.id <= maxAssignableId),
    [allData.permissions, maxAssignableId],
  );

  const filteredPermissionManagements = useMemo(
    () => (allData.permission_managements || []).filter(
      (item) => item.function_name === ACTIVE_FUNCTION,
    ),
    [allData.permission_managements],
  );

  const supportingDataMap = useMemo(() => ({
    permission_managements: {
      users: allData.users || [],
      functions: allData.functions || [],
      permissions: assignablePermissions,
      defaultFunction: ACTIVE_FUNCTION,
      defaultFunctionId: functionRecord?.id || null,
    },
  }), [allData, assignablePermissions, functionRecord]);

  const handleReload = useCallback(async (masterKey) => {
    try {
      const def = USER_DEFINITIONS.find((d) => d.key === masterKey);
      /* istanbul ignore next -- masterKey は常に USER_DEFINITIONS 上のキーのため到達不能 */
      if (!def) return;
      const items = await getMasterItems(def.endpoint);
      setAllData((prev) => ({ ...prev, [masterKey]: items }));
    } catch (_err) { /* ignore */ }
  }, []);

  const getItemsForKey = (key) => {
    if (key === 'permission_managements') return filteredPermissionManagements;
    return allData[key] || [];
  };

  const activeDef = activeKey ? getMasterDefinition(activeKey) : null;

  return (
    <DashboardPageFrame
      mainClassName="master-main"
      primaryTabActiveIndex={2}
      primaryTabs={getFeatureTabs()}
      rootClassName="master-page"
      sectionLabel=""
      stickyAreaClassName="master-sticky-area"
    >
      <section className="master-panel">
        {loading && <div className="master-empty-state">読み込み中...</div>}
        {!loading && error && <div className="master-alert master-alert--error">{error}</div>}

        {!loading && !error && USER_GROUP && (
          <div className="master-groups-grid">
            <div className={`master-group master-group--${USER_GROUP.colorClass}`}>
              <div className="master-group-header">
                <h3 className="master-group-title">{USER_GROUP.label}</h3>
              </div>
              <div className="master-group-cards">
                {USER_GROUP.keys.map((key) => {
                  const def = getMasterDefinition(key);
                  /* istanbul ignore next -- USER_GROUP.keys は全て定義が存在するため到達不能 */
                  if (!def) return null;
                  const count = getItemsForKey(key).length;
                  return (
                    <button
                      className="master-table-card"
                      key={key}
                      onClick={() => setActiveKey(key)}
                      type="button"
                    >
                      <span className="master-table-card-label">{def.label}</span>
                      <span className="master-table-card-count">
                        {count}<span className="master-table-card-unit">件</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeDef && (
          <div className="ml-overlay" onClick={() => setActiveKey(null)}>
            <div className="master-table-popup" onClick={(e) => e.stopPropagation()}>
              <div className="master-table-popup-header">
                <h3 className="master-table-popup-title">{activeDef.label}</h3>
                <button
                  className="master-table-popup-close"
                  onClick={() => setActiveKey(null)}
                  type="button"
                >
                  ✕
                </button>
              </div>
              <div className="master-table-popup-body">
                <MasterCrudTable
                  definition={activeDef}
                  items={getItemsForKey(activeKey)}
                  onReload={() => handleReload(activeKey)}
                  readOnly={SUPER_ADMIN_KEYS.has(activeKey) && !canSuperAdmin(ACTIVE_FUNCTION)}
                  supportingData={supportingDataMap[activeKey] || {}}
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </DashboardPageFrame>
  );
}
